import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * SettingsCheckbox — the v7 permissions-matrix checkbox (SCRUM-457).
 *
 * Token-faithful port of the prototype `.settings-checkbox` (app-shell 7.html):
 * a visually-hidden native <input> drives an 18×18 box (rounded --r-sm, bg
 * --bg-3, 1px --border-1). Checked → box fills/borders --cyan and a ::after
 * pseudo draws the tick: a 10×6 rect with 2px left/bottom borders in --bg-4,
 * rotated -45° and nudged translate(1px,-1px). The tick is reproduced with a
 * real child <span> (React can't target the CSS ::after) sized identically;
 * its checked visibility is driven from the peer <input> via a nested
 * `peer-checked:[&_[data-tick]]:block` selector on the box.
 *
 * Token map: --bg-3→bg-bg-3, --border-1→border-border-1, --r-sm→rounded-tk-sm,
 * --cyan→brand-primary, --bg-4→bg-bg-4, --duration-fast(120ms)/--ease-out.
 * Uncontrolled or controlled via the standard `checked`/`onChange` input props.
 */
export type SettingsCheckboxProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'type' | 'className'
> & {
  /** Class applied to the outer <label> wrapper. */
  className?: string;
};

export const SettingsCheckbox = React.forwardRef<HTMLInputElement, SettingsCheckboxProps>(
  function SettingsCheckbox({ className, ...inputProps }, ref) {
    return (
      <label className={cn('relative inline-flex cursor-pointer', className)}>
        <input
          ref={ref}
          type="checkbox"
          className="peer pointer-events-none absolute opacity-0"
          {...inputProps}
        />
        <span
          className={cn(
            'bg-bg-3 border-border-1 rounded-tk-sm inline-flex h-[18px] w-[18px] items-center justify-center border transition-all duration-[120ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
            'peer-checked:bg-brand-primary peer-checked:border-brand-primary',
            'peer-checked:[&_[data-tick]]:block',
          )}
        >
          <span
            data-tick
            aria-hidden="true"
            className="hidden h-[6px] w-[10px] border-b-2 border-l-2 border-b-[var(--bg-4)] border-l-[var(--bg-4)]"
            style={{ transform: 'rotate(-45deg) translate(1px, -1px)' }}
          />
        </span>
      </label>
    );
  },
);
